/**
 * BUDB PKR landing — product carousel.
 * Only one photo slide uses poker-sol-pro (man at table); rest are artistic plates.
 * Slides = tables · slots · blackjack · account · web3 · install.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type TouchEvent,
} from "react";
import { useLocation } from "wouter";
import {
  CardTableIcon,
  SlotMachineIcon,
  GbuxTokenIcon,
  TrophyBadgeIcon,
  ZapBoltIcon,
  ShieldLockIcon,
} from "@/components/icons";
import { GBUX, ACCOUNT_LINKS, CROSSMINT_FLEET } from "@shared/fleet";
import { BRAND } from "@shared/brand";
import { SiteFooter } from "@/components/site-footer";
import { LandingTableDemo } from "@/components/landing-table-demo";
import { BuyGbuxModal } from "@/components/buy-gbux-modal";
import { useWalletState } from "@/contexts/wallet-context";
import { loginWithGrudgeId } from "@/lib/grudge-auth";
import {
  isInPhantomInAppBrowser,
  isMobileBrowser,
  openDappInPhantomBrowser,
  phantomDappUrl,
  solflareMobileBrowse,
} from "@/lib/mobile-web3";
import "@/styles/landing.css";

type SlideCta = {
  label: string;
  href?: string;
  action?:
    | "buy"
    | "pwa"
    | "deposit"
    | "swap"
    | "crossmint"
    | "login"
    | "phantom_dapp"
    | "solflare_dapp";
};

type Slide = {
  id: string;
  kind: "hero" | "product" | "table" | "web3" | "install";
  kicker: string;
  title: ReactNode;
  desc: string;
  /** Artistic bg — only welcome uses photo pro plate */
  bg?: string;
  color?: string;
  icon?: ReactNode;
  bullets?: string[];
  ctas?: SlideCta[];
};

function PhoneIcon({ size = 28, color = "#00ff88" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="7" y="2" width="10" height="20" rx="2" stroke={color} strokeWidth="1.8" />
      <circle cx="12" cy="18.5" r="1" fill={color} />
    </svg>
  );
}

/** Artistic plates only (no second “man at table” photo) */
const ART = {
  felt: BRAND.tableHero,
  greenFelt: BRAND.felts.green,
  panel: BRAND.panels.feltPattern,
  wood: BRAND.panels.wood,
  holdem: BRAND.features.holdem,
  web3: BRAND.features.web3,
  accounts: BRAND.features.accounts,
  rewards: BRAND.features.rewards,
  install: BRAND.features.install,
  ai: BRAND.features.aiEdge,
  slots: BRAND.slots.ambient,
  slotsAlt: BRAND.slots.ambientAlt,
  /** ONLY photo of player at table — used once */
  photoPro: BRAND.pokerSolPro,
  /** BUDB × Grudge generated hero art (David × Racalvin showdown) */
  budbxGrudge: "/media/budbxgrudge-slide.png",
} as const;

const SLIDES: Slide[] = [
  {
    id: "welcome",
    kind: "hero",
    kicker: "Grudge Studio · Solana mainnet",
    title: (
      <>
        {BRAND.name}
        <br />
        <span>Texas Hold&apos;em · real GBUX</span>
      </>
    ),
    desc: `${BRAND.tagline}. One production stack — tables, slots, blackjack, account.`,
    bg: ART.photoPro, // sole “man playing poker” image
    ctas: [
      { label: "Open in Phantom", action: "phantom_dapp" },
      { label: "Play now", href: "/lobby" },
      { label: "Buy GBUX", action: "buy" },
    ],
  },
  {
    id: "budbxgrudge",
    kind: "product",
    kicker: "BUDB × GRUDGE",
    title: (
      <>
        BUDBxGRUDGE
        <br />
        <span>BetUDontBet · Grudge Studio</span>
      </>
    ),
    desc:
      "Generated showdown art: human skill meets Racalvin energy under the Grudge neon. Same mainnet GBUX — tables, slots, blackjack, house agent bank.",
    bg: ART.budbxGrudge,
    color: "#c084fc",
    icon: <ZapBoltIcon size={28} color="#c084fc" />,
    bullets: [
      "BUDB PKR × Grudge Studio — one fleet brand",
      "Grudge ID · Phantom Connect · play ledger",
      "HU vs BUDB · Racalvin dealer · agent house bank",
      "Open.grudge · id.grudge · poker.grudge — same account path",
    ],
    ctas: [
      { label: "Play now", href: "/lobby" },
      { label: "GBUX Games", href: "/gbuxgames" },
      { label: "HU vs BUDB", href: "/table/hu-vs-budb" },
    ],
  },
  {
    id: "tables",
    kind: "product",
    kicker: "Lobby · live tables",
    title: "Tables available",
    desc: "Cash NL & heads-up rooms — join from the lobby with play GBUX.",
    bg: ART.greenFelt,
    color: "#66a3ff",
    icon: <CardTableIcon size={28} color="#66a3ff" />,
    bullets: [
      "NL 1/2 · 2/4 · 5/10 · 50/100",
      "Heads-up matching stakes",
      "WebSocket multiplayer · edge Durable Objects",
      "Buy-in from play ledger only",
    ],
    ctas: [
      { label: "Open lobby", href: "/lobby" },
      { label: "Table demo", href: "#demo" },
    ],
  },
  {
    id: "table-demo",
    kind: "table",
    kicker: "Table UI · Calling Pros",
    title: "Full deal · raise · call",
    desc: "Large cards, pro seats, UTG+1 raise → Button re-raise → BB call → flop.",
    ctas: [
      { label: "Sit at a table", href: "/lobby" },
      { label: "Calling Pros", href: "/callingpros" },
    ],
  },
  {
    id: "slots",
    kind: "product",
    kicker: "GBUX slots",
    title: BRAND.slots.title,
    desc: `${BRAND.slots.subtitle}. 5×5 · Most Wanted bounties · play ledger spins.`,
    bg: ART.slots,
    color: "#f0c14b",
    icon: <SlotMachineIcon size={28} color="#f0c14b" />,
    bullets: [
      "Ethereal Treasure mesh cabinet",
      "Grudge Most Wanted jackpot tiers",
      "Malachar free-spin bounty",
      "Same play GBUX as poker tables",
    ],
    ctas: [
      { label: "Play slots", href: "/slots" },
      { label: "Fund play", action: "deposit" },
    ],
  },
  {
    id: "blackjack",
    kind: "product",
    kicker: "Casino · GBUX",
    title: "Blackjack",
    desc: "3-deck shoe · three hands · debit play ledger like tables.",
    bg: ART.holdem,
    color: "#34d399",
    icon: <CardTableIcon size={28} color="#34d399" />,
    bullets: [
      "Hit · stand · double",
      "Real GBUX stakes",
      "Edge session state",
    ],
    ctas: [
      { label: "Play blackjack", href: "/blackjack" },
      { label: "Open lobby", href: "/lobby" },
    ],
  },
  {
    id: "account",
    kind: "product",
    kicker: "You · play ledger",
    title: "Account info",
    desc: "Balances, ledger, appearance, send play GBUX by username.",
    bg: ART.accounts,
    color: "#cc88ff",
    icon: <ShieldLockIcon size={28} color="#cc88ff" />,
    bullets: [
      "Play · on-chain · fleet bag",
      "Hand history & sessions",
      "Send: server wallet ↔ server wallet",
      "Cash out / swap: games@grudgestudio.org",
    ],
    ctas: [
      { label: "My account", href: "/account" },
      { label: "Grudge ID", action: "login" },
    ],
  },
  {
    id: "web3",
    kind: "web3",
    kicker: "Web3 · mainnet only",
    title: "GBUX money stack",
    desc: "Buy · deposit on-chain. Play Send is server-side only. Cash out / swap → games@grudgestudio.org.",
    bg: ART.web3,
  },
  {
    id: "pros",
    kind: "product",
    kicker: "Badges",
    title: "Calling Pros",
    desc: "25 legend badges. Admin AI holds until a pro claims.",
    bg: ART.rewards,
    color: "#fbbf24",
    icon: <TrophyBadgeIcon size={28} color="#fbbf24" />,
    bullets: [
      "Animated seat rings",
      "Claim with Solana wallet",
      "One badge per player",
    ],
    ctas: [
      { label: "Claim badges", href: "/callingpros" },
      { label: "Play now", href: "/lobby" },
    ],
  },
  {
    id: "install",
    kind: "install",
    kicker: "Phone dApp",
    title: "Open in Phantom · play GBUX",
    desc: "Best practice on phones: run the live site inside Phantom (or Solflare) for inject wallet, fund play, swap, and signs — not a separate store app.",
    bg: ART.install,
    color: "#a78bfa",
    icon: <PhoneIcon size={28} color="#a78bfa" />,
    bullets: [
      "Open in Phantom app browser (full dApp)",
      "Fund play · swap · sit tables on mainnet",
      "Optional: PWA home screen after first connect",
    ],
    ctas: [
      { label: "Open in Phantom", action: "phantom_dapp" },
      { label: "Open lobby", href: "/lobby" },
    ],
  },
];

export default function Landing() {
  const [, setLocation] = useLocation();
  const { openWalletPanel } = useWalletState();
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
  const [installed, setInstalled] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);

  const n = SLIDES.length;
  const go = useCallback(
    (dir: number) => setSlide((s) => (s + dir + n) % n),
    [n],
  );

  useEffect(() => {
    if (paused) return;
    const t = window.setInterval(() => go(1), 7000);
    return () => window.clearInterval(t);
  }, [go, paused]);

  useEffect(() => {
    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", () => setInstalled(true));
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
    }
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const installPwa = async () => {
    const ev = deferredPrompt as { prompt?: () => Promise<void> } | null;
    if (ev?.prompt) {
      await ev.prompt();
      setDeferredPrompt(null);
      return;
    }
    document.getElementById("install")?.scrollIntoView({ behavior: "smooth" });
  };

  const openPhantomDapp = () => {
    openDappInPhantomBrowser(phantomDappUrl("/lobby"));
  };

  const runCta = (c: SlideCta) => {
    if (c.action === "buy") setBuyOpen(true);
    else if (c.action === "pwa") void installPwa();
    else if (c.action === "phantom_dapp") openPhantomDapp();
    else if (c.action === "solflare_dapp") {
      solflareMobileBrowse(phantomDappUrl("/lobby"));
    } else if (c.action === "deposit") openWalletPanel("transfer");
    else if (c.action === "swap") openWalletPanel("swap");
    else if (c.action === "crossmint") setLocation("/connect");
    else if (c.action === "login") loginWithGrudgeId("/account");
    else if (c.href?.startsWith("#")) {
      document.getElementById(c.href.slice(1))?.scrollIntoView({ behavior: "smooth" });
      if (c.href === "#demo") {
        const idx = SLIDES.findIndex((x) => x.id === "table-demo");
        if (idx >= 0) setSlide(idx);
      }
    } else if (c.href) setLocation(c.href);
  };

  const mintUrl = `https://solscan.io/token/${GBUX.mint}`;
  const agentUrl = `https://solscan.io/account/${CROSSMINT_FLEET.agentWallet}`;

  const touch = useMemo(() => {
    let x0 = 0;
    return {
      onTouchStart: (e: TouchEvent) => {
        x0 = e.touches[0]?.clientX ?? 0;
        setPaused(true);
      },
      onTouchEnd: (e: TouchEvent) => {
        const x1 = e.changedTouches[0]?.clientX ?? x0;
        const d = x1 - x0;
        if (Math.abs(d) > 48) go(d < 0 ? 1 : -1);
        setPaused(false);
      },
    };
  }, [go]);

  return (
    <div className="landing-page">
      <header
        className="landing-hero"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        {...touch}
      >
        <nav className="landing-hero__nav">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img
              src={BRAND.logo}
              alt={BRAND.name}
              width={40}
              height={40}
              style={{ borderRadius: 10, objectFit: "cover" }}
            />
            <span
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 26,
                letterSpacing: 1,
              }}
            >
              {BRAND.shortName}
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <button type="button" className="landing-btn-nav" onClick={() => setLocation("/lobby")}>
              Tables
            </button>
            <button type="button" className="landing-btn-nav" onClick={() => setLocation("/slots")}>
              Slots
            </button>
            <button
              type="button"
              className="landing-btn-nav"
              onClick={() => setLocation("/blackjack")}
            >
              Blackjack
            </button>
            <button
              type="button"
              className="landing-btn-nav"
              onClick={() => setLocation("/account")}
            >
              Account
            </button>
            <button type="button" className="landing-btn-nav" onClick={() => setBuyOpen(true)}>
              Buy GBUX
            </button>
          </div>
        </nav>

        <div className="landing-hero__stage">
          {SLIDES.map((s, i) => {
            const isActive = i === slide;

            if (s.kind === "table") {
              return (
                <div
                  key={s.id}
                  id={s.id === "table-demo" ? "demo" : undefined}
                  className={`landing-slide ${isActive ? "is-active" : ""}`}
                  aria-hidden={!isActive}
                >
                  <LandingTableDemo />
                  <div className="landing-slide__scrim" />
                  <div className="landing-slide__body">
                    <p className="landing-slide__kicker">{s.kicker}</p>
                    <h1 className="landing-slide__title">{s.title}</h1>
                    <p className="landing-slide__desc">{s.desc}</p>
                    <div className="landing-slide__ctas">
                      {(s.ctas ?? []).map((c) => (
                        <button
                          key={c.label}
                          type="button"
                          className={
                            c === s.ctas![0]
                              ? "landing-btn-primary"
                              : "landing-btn-secondary"
                          }
                          onClick={() => runCta(c)}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            if (s.kind === "web3") {
              return (
                <div
                  key={s.id}
                  className={`landing-slide ${isActive ? "is-active" : ""}`}
                  aria-hidden={!isActive}
                >
                  <div
                    className="landing-slide__bg"
                    style={{ backgroundImage: `url(${s.bg})` }}
                  />
                  <div className="landing-slide__scrim" />
                  <div className="landing-slide__fx" aria-hidden />
                  <div className="landing-slide__glow" aria-hidden />
                  <div className="landing-slide__body landing-web3">
                    <div className="landing-slide__icon-row">
                      <div
                        className="landing-slide__icon"
                        style={{
                          borderColor: "#00eeff66",
                          boxShadow: "0 0 24px #00eeff33",
                        }}
                      >
                        <GbuxTokenIcon size={28} color="#00eeff" />
                      </div>
                    </div>
                    <p className="landing-slide__kicker">{s.kicker}</p>
                    <h1 className="landing-slide__title">{s.title}</h1>
                    <p className="landing-slide__desc">{s.desc}</p>
                    <div className="landing-web3__grid">
                      <div className="landing-web3__chip">
                        <strong>GBUX</strong>
                        <span>
                          ${GBUX.rateUsd}/token · SPL · {GBUX.network}
                        </span>
                      </div>
                      <div className="landing-web3__chip">
                        <strong>Buy</strong>
                        <span>SOL or USDT → play (5% fee)</span>
                      </div>
                      <div className="landing-web3__chip">
                        <strong>Deposit</strong>
                        <span>Phantom SPL → BUDBai → play 1:1</span>
                      </div>
                      <div className="landing-web3__chip">
                        <strong>Swap</strong>
                        <span>Fleet SOL↔GBUX live exchange</span>
                      </div>
                      <div className="landing-web3__chip">
                        <strong>Phantom</strong>
                        <span>Connect · how you sign</span>
                      </div>
                      <div className="landing-web3__chip">
                        <strong>BUDBai</strong>
                        <span>Admin agent AI deposit</span>
                      </div>
                    </div>
                    <div className="landing-web3__links">
                      <button
                        type="button"
                        className="landing-btn-primary"
                        onClick={() => setBuyOpen(true)}
                      >
                        Buy GBUX
                      </button>
                      <button
                        type="button"
                        className="landing-btn-secondary"
                        onClick={() => openWalletPanel("transfer")}
                      >
                        Deposit play
                      </button>
                      <button
                        type="button"
                        className="landing-btn-secondary"
                        onClick={() => openWalletPanel("swap")}
                      >
                        Swap
                      </button>
                      <button
                        type="button"
                        className="landing-btn-secondary"
                        onClick={() => setLocation("/connect")}
                      >
                        Phantom Connect
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={s.id}
                className={`landing-slide ${isActive ? "is-active" : ""}`}
                aria-hidden={!isActive}
              >
                <div
                  className="landing-slide__bg"
                  style={{ backgroundImage: `url(${s.bg})` }}
                />
                <div className="landing-slide__scrim" />
                <div className="landing-slide__fx" aria-hidden />
                <div className="landing-slide__glow" aria-hidden />
                <div className="landing-slide__body">
                  {s.icon && (
                    <div className="landing-slide__icon-row">
                      <div
                        className="landing-slide__icon"
                        style={{
                          borderColor: `${s.color ?? "#f0c14b"}66`,
                          boxShadow: `0 0 24px ${s.color ?? "#f0c14b"}33`,
                        }}
                      >
                        {s.icon}
                      </div>
                    </div>
                  )}
                  <p className="landing-slide__kicker">{s.kicker}</p>
                  <h1 className="landing-slide__title">{s.title}</h1>
                  <p className="landing-slide__desc">{s.desc}</p>
                  {s.bullets && s.bullets.length > 0 && (
                    <ul className="landing-slide__bullets">
                      {s.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  )}
                  <div className="landing-slide__ctas">
                    {(s.ctas ?? []).map((c, ci) => (
                      <button
                        key={c.label}
                        type="button"
                        className={
                          ci === 0 ? "landing-btn-primary" : "landing-btn-secondary"
                        }
                        onClick={() => runCta(c)}
                      >
                        {c.action === "pwa" && installed ? "Installed ✓" : c.label}
                      </button>
                    ))}
                  </div>
                  {s.kind === "hero" && (
                    <p
                      style={{
                        marginTop: 12,
                        fontSize: 12,
                        color: "rgba(255,255,255,0.45)",
                      }}
                    >
                      {GBUX.symbol} · {GBUX.network} · mint {GBUX.mint.slice(0, 8)}…
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="landing-arrow landing-arrow--prev"
          aria-label="Previous slide"
          onClick={() => go(-1)}
        >
          ‹
        </button>
        <button
          type="button"
          className="landing-arrow landing-arrow--next"
          aria-label="Next slide"
          onClick={() => go(1)}
        >
          ›
        </button>

        <div className="landing-hero__controls" role="tablist" aria-label="Landing slides">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === slide}
              className={`landing-dot ${i === slide ? "is-active" : ""}`}
              title={typeof s.title === "string" ? s.title : s.id}
              onClick={() => setSlide(i)}
            />
          ))}
        </div>
      </header>

      {/* Product map strip */}
      <section className="landing-section" style={{ paddingBottom: 8 }}>
        <h2>What you can open</h2>
        <div className="landing-dl-grid">
          {[
            { t: "Tables", d: "NL & HU cash · play GBUX", h: "/lobby", icon: <CardTableIcon size={22} color="#66a3ff" /> },
            { t: "GBUX Games", d: "Slots · Blackjack · one wallet", h: "/gbuxgames", icon: <SlotMachineIcon size={22} color="#f0c14b" /> },
            { t: "Blackjack", d: "3 hands · CSPRNG shoe", h: "/blackjack", icon: <CardTableIcon size={22} color="#34d399" /> },
            { t: "Account", d: "Ledger · avatar · send GBUX", h: "/account", icon: <ShieldLockIcon size={22} color="#cc88ff" /> },
            { t: "Calling Pros", d: "Claim legend badges", h: "/callingpros", icon: <TrophyBadgeIcon size={22} color="#fbbf24" /> },
            { t: "Connect", d: "Grudge ID · Phantom · play", h: "/connect", icon: <ZapBoltIcon size={22} color="#00eeff" /> },
          ].map((x) => (
            <button
              key={x.t}
              type="button"
              className="landing-dl-card"
              style={{ cursor: "pointer", textAlign: "left", color: "inherit" }}
              onClick={() => setLocation(x.h)}
            >
              <div className="landing-dl-icon">{x.icon}</div>
              <h3>{x.t}</h3>
              <p>{x.d}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="landing-web3-section" id="web3">
        <div className="landing-web3-panel">
          <h2>Web3 GBUX · live production</h2>
          <p>
            Solana <strong style={{ color: "#f0c14b" }}>{GBUX.network}</strong> only.
            <strong>Grudge ID</strong> who you are · <strong>Phantom Connect</strong> how you
            sign · <strong>play ledger</strong> how the table runs.
          </p>
          <div className="landing-web3-actions">
            <button
              type="button"
              className="landing-web3-action"
              onClick={() => loginWithGrudgeId("/lobby")}
            >
              <strong>Grudge ID</strong>
              <span>Who you are · id.grudge-studio.com</span>
            </button>
            <button
              type="button"
              className="landing-web3-action"
              onClick={() => setLocation("/connect")}
            >
              <strong>Phantom Connect</strong>
              <span>Extension · Google · Apple</span>
            </button>
            <button
              type="button"
              className="landing-web3-action"
              onClick={() => setLocation("/lobby")}
            >
              <strong>Play ledger</strong>
              <span>Sit tables · 200 welcome GBUX</span>
            </button>
            <button type="button" className="landing-web3-action" onClick={() => setBuyOpen(true)}>
              <strong>Buy more GBUX</strong>
              <span>SOL or USDT · live quote</span>
            </button>
            <button
              type="button"
              className="landing-web3-action"
              onClick={() => openWalletPanel("transfer")}
            >
              <strong>Deposit SPL</strong>
              <span>Phantom → play 1:1</span>
            </button>
            <button
              type="button"
              className="landing-web3-action"
              onClick={() => window.open(mintUrl, "_blank", "noopener")}
            >
              <strong>GBUX on Solscan</strong>
              <span>Mint explorer</span>
            </button>
            <button
              type="button"
              className="landing-web3-action"
              onClick={() => window.open(agentUrl, "_blank", "noopener")}
            >
              <strong>BUDBai agent</strong>
              <span>Deposit address</span>
            </button>
            <button
              type="button"
              className="landing-web3-action"
              onClick={() => setLocation("/account")}
            >
              <strong>Account ledger</strong>
              <span>Play · history · P2P</span>
            </button>
          </div>
        </div>
      </section>

      <section className="landing-section" id="install">
        <h2>Phone app · Phantom dApp</h2>
        <div className="landing-install-note space-y-3">
          <p className="text-sm text-zinc-300 leading-relaxed m-0">
            There is no separate store binary. On phones,{" "}
            <strong className="text-violet-200">open this live site inside Phantom</strong>{" "}
            (or Solflare) for inject wallet, fund play GBUX, swaps, and table
            signs — same production stack as desktop.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
            <button
              type="button"
              className="landing-btn-primary"
              style={{
                background: "linear-gradient(135deg, #6d28d9, #8b5cf6)",
                border: "none",
              }}
              onClick={openPhantomDapp}
            >
              {isInPhantomInAppBrowser()
                ? "Continue in Phantom → Lobby"
                : "Open app in Phantom"}
            </button>
            <button
              type="button"
              className="landing-btn-secondary"
              onClick={() => solflareMobileBrowse(phantomDappUrl("/lobby"))}
            >
              Open in Solflare
            </button>
            <button
              type="button"
              className="landing-btn-secondary"
              onClick={() => setLocation("/lobby")}
            >
              Browser lobby
            </button>
            {(deferredPrompt || !installed) && (
              <button
                type="button"
                className="landing-btn-secondary"
                onClick={() => void installPwa()}
              >
                {installed ? "Home screen ✓" : "PWA home screen"}
              </button>
            )}
          </div>
          <p className="text-[11px] text-zinc-500 m-0 leading-snug">
            Optional PWA: iOS Safari → Share → Add to Home Screen · Android Chrome
            → Install · Desktop install icon. After Phantom open, use Wallet →
            Fund play / Transfer for GBUX.
          </p>
        </div>
      </section>

      <SiteFooter />
      <BuyGbuxModal open={buyOpen} onOpenChange={setBuyOpen} />
    </div>
  );
}
